import { createContext, useContext, useEffect, useMemo, useRef, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import i18n from "./index";
import { DEFAULT_LANG, Lang, parseLangPath, translatePath, localizedHref as buildHref } from "./paths";

type Ctx = {
  lang: Lang;
  switchTo: (target: Lang) => string; // returns the path to navigate to
  href: (svPath: string) => string;   // localized href for current language
};

const LanguageContext = createContext<Ctx>({
  lang: DEFAULT_LANG,
  switchTo: () => "/",
  href: (p) => p,
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { lang } = parseLangPath(pathname);
  const autoChecked = useRef(false);

  // One-shot: redirect non-Swedish browsers landing on a Swedish path to /en.
  useEffect(() => {
    if (autoChecked.current) return;
    autoChecked.current = true;
    if (typeof window === "undefined") return;
    try {
      const stored = localStorage.getItem("flyg.lang");
      if (stored) return; // user has a preference already
      if (lang !== "sv") return; // already on a localized path
      const nav = (navigator.language || "").toLowerCase();
      const langs = (navigator.languages || [navigator.language || ""]).map((l) => l.toLowerCase());
      const prefersSwedish = nav.startsWith("sv") || langs.some((l) => l.startsWith("sv"));
      if (prefersSwedish) return;
      const target = translatePath(pathname + window.location.search + window.location.hash, "en");
      navigate(target, { replace: true });
    } catch {
      /* ignore */
    }
  }, [lang, pathname, navigate]);

  useEffect(() => {
    if (i18n.language !== lang) i18n.changeLanguage(lang);
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
    try { localStorage.setItem("flyg.lang", lang); } catch { /* ignore */ }
  }, [lang]);

  const value = useMemo<Ctx>(() => ({
    lang,
    switchTo: (target) => translatePath(pathname, target),
    href: (svPath) => buildHref(svPath, lang),
  }), [lang, pathname]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  return useContext(LanguageContext);
}